import { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { AdminContext } from 'context/AdminContext'
import { OrganizationBillingContext } from 'context/OrganizationBillingContext'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import moment from "moment";

import ScreenFrame from "components/Frame/ScreenFrame";
import ContentFrame from "components/Frame/ContentFrame";
import SolidButton from "components/Buttons/SolidButton";
import OrganizationFreeAthletes from 'components/Billing/OrganizationFreeAthletes'
import OrganizationTrialingAthletes from "components/Billing/OrganizationTrialingAthletes";
import OrganizationPausedAthletes from "components/Billing/OrganizationPausedAthletes";
import OrganizationProratedAthletes from "components/Billing/OrganizationProratedAthletes";
import OrganizationActiveAthletes from "components/Billing/OrganizationActiveAthletes";
import OrganizationInvoicePreview from 'components/Billing/OrganizationInvoicePreview'
import OrganizationBillingConfiguration from 'components/Billing/OrganizationBillingConfiguration'
import OrganizationInactiveAthletes from 'components/Billing/OrganizationInactiveAthletes'
import OrganizationPaymentMethod from 'components/Billing/OrganizationPaymentMethod'
import OrganizationInvoiceHistory from 'components/Billing/OrganizationInvoiceHistory'

export default function BillingOverview() {

    const userType = useSelector((state) => state.user.type)
    const adminToolsVisible = useSelector((state) => state.user.adminToolsVisible)
    const organizationName = useSelector((state) => state.user.organizationName)
    const organizationHandlesMembership = useSelector((state) => state.user.organizationHandlesMembership)
    const billingLocalTimestamp = useSelector((state) => state.organization.billingLocalTimestamp)

    const { ShowEditAthleteMembershipModal, ShowEditOrganizationBillingModal } = useContext(AdminContext)
    const { GetBillingDataFromBackend, ShowOffboardAthleteModal } = useContext(OrganizationBillingContext)

    const canAccessSidebarSalesTools = ff(flags.canAccessSidebarSalesTools, { userType: userType, adminToolsVisible: adminToolsVisible })

    const [billingData, setBillingData] = useState(null);

    useEffect(() => { GetBillingDataFromBackend(setBillingData) }, [])

    if (organizationHandlesMembership === 0) {
        return (
            <ScreenFrame>
                <div className="flex-3 mx-auto max-w-7xl py-12 px-8 space-y-6">
                    <h1 className="text-center text-xl font-semibold text-gray-900">Athletes in {organizationName} handle their own subscriptions</h1>
                    <h1 className="text-center text-gray-600">Please reach out to support@kinesisintegrated.com with any questions</h1>
                </div>
            </ScreenFrame>
        )
    }

    if (billingData === null) {
        return (
            <ScreenFrame>
                <div className='w-2/3 mx-auto space-y-4'>
                    <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='max-w-4xl mx-auto space-y-4'>
                <div className="flex items-center">                
                    <div className="flex-1 space-y-1">
                        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Billing</h1>
                        <p className="text-sm text-gray-500 font-medium">Preview upcoming bill, manage your organization payment settings, etc.</p>
                    </div>

                    {canAccessSidebarSalesTools &&
                        <div className="w-32">
                            <SolidButton
                                text="Edit"
                                action={() => ShowEditOrganizationBillingModal()}
                                className='shadow-md'
                            />
                        </div>
                    }
                </div>

                <div className="pt-2 flex items-center">
                    <div className="flex-1">
                        {billingLocalTimestamp.format('MMMM YYYY')} Bill
                    </div>

                    {billingData?.organizationBillingInfo?.monthlyBillingPeriod === "start" &&
                        <div className="text-sm text-gray-600">Auto billing on {billingLocalTimestamp.clone().add(1, 'months').startOf('month').format('MMMM D, YYYY')}</div>
                    }
                    {billingData?.organizationBillingInfo?.monthlyBillingPeriod === "end" &&
                        <div className="text-sm text-gray-600">Auto billing on {billingLocalTimestamp.clone().endOf('month').format('MMMM D, YYYY')}</div>
                    }
                </div>

                <ContentFrame>
                    <div className="w-full divide-y divide-gray-200">
                         <OrganizationFreeAthletes 
                            freeSeatAthletes={billingData?.itemizedReceipt?.athletes.filter(athlete => athlete.status === 'free-seat') || []}
                            ShowEditAthleteMembershipModal={ShowEditAthleteMembershipModal}
                            ShowOffboardAthleteModal={ShowOffboardAthleteModal}
                        />

                        <OrganizationTrialingAthletes 
                            freeTrialingAthletes={billingData?.itemizedReceipt?.athletes?.filter(athlete => athlete.status === 'free-trial').sort((a, b) => new moment(a.freeTrialEndDate) - new moment(b.freeTrialEndDate)) || []}
                            ShowEditAthleteMembershipModal={ShowEditAthleteMembershipModal}
                            ShowOffboardAthleteModal={ShowOffboardAthleteModal}
                        />

                        <OrganizationPausedAthletes 
                            pausedAthletes={billingData?.itemizedReceipt?.athletes?.filter(athlete => athlete.status === 'pause').sort((a, b) => new moment(a.pauseEndDate) - new moment(b.pauseEndDate)) || []}
                            ShowEditAthleteMembershipModal={ShowEditAthleteMembershipModal}
                            ShowOffboardAthleteModal={ShowOffboardAthleteModal}
                        />

                        <OrganizationActiveAthletes 
                            activeAthletes={billingData?.itemizedReceipt?.athletes?.filter(athlete => (athlete.status === 'active' || athlete.status === 'prorated-onboard')).sort((a, b) => a.price - b.price) || []}
                            ShowEditAthleteMembershipModal={ShowEditAthleteMembershipModal}
                            ShowOffboardAthleteModal={ShowOffboardAthleteModal}
                        />

                        <OrganizationProratedAthletes 
                            proratedType="offboard"
                            proratedAthletes={billingData?.itemizedReceipt?.athletes?.filter(athlete => athlete.status === 'prorated-offboard').sort((a, b) => a.price - b.price) || []}
                            ShowEditAthleteMembershipModal={ShowEditAthleteMembershipModal}
                        />

                        <OrganizationInvoicePreview 
                            athleteTotal={billingData?.itemizedReceipt?.athleteTotal || 0}
                            minimumRevenueClauseDifference={billingData?.itemizedReceipt?.minimumRevenueClauseDifference || 0}
                            enableProcessingFee={billingData?.organizationBillingInfo?.enableProcessingFee || 0}
                            processingFee={billingData?.itemizedReceipt?.processingFee || 0} 
                        />
                    </div>
                </ContentFrame>

                <div className="pt-2">Billing Settings</div>

                <ContentFrame>
                    <div className="w-full divide-y divide-gray-200">
                        <OrganizationBillingConfiguration billingData={billingData} />

                        {billingData?.organizationBillingInfo?.minimumRevenueClauseAmount > 0 &&
                            <div className='p-4 flex items-center cursor-pointer'>
                                <div>Minimum Revenue Clause</div>
                                <div className="flex-1" />
                                <div className="text-sm text-gray-500">Minimum payment of ${billingData?.organizationBillingInfo?.minimumRevenueClauseAmount.toFixed(2)} required</div>
                            </div>
                        }

                        <OrganizationInactiveAthletes 
                            inactiveAthletes={billingData?.itemizedReceipt?.athletes?.filter(athlete => athlete.status === 'inactive') || []} 
                        />

                        <OrganizationInvoiceHistory invoiceHistory={billingData?.organizationStripeData?.invoiceHistory || []} />

                        <OrganizationPaymentMethod billingData={billingData} />

                        <div 
                            className="p-4 flex items-center"
                        >
                            <div className="pr-2">Organization Owner</div>
                            <div className="flex-1" />
                            <div>{billingData?.organizationBillingInfo?.adminFirstName} {billingData?.organizationBillingInfo?.adminLastName} <span className="pl-1 text-sm text-gray-500">({billingData?.organizationBillingInfo?.adminEmail})</span></div>
                        </div>
                    </div>
                </ContentFrame>
            </div>
        </ScreenFrame>
    )
}
